import { useEffect, useState } from 'react';
import { Brain, FloppyDisk, ArrowCounterClockwise, Plus, Trash, ArrowUp, ArrowDown, Play, Sparkle, Warning, Check, ArrowsClockwise, CircleNotch } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ kind, children }) {
  const styles = kind === 'success'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : kind === 'error'
    ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-blue-50 border-blue-200 text-blue-800';
  return <div className={`px-3 py-2 text-xs rounded-sm border flex items-center gap-2 ${styles}`}>{children}</div>;
}

function makeNewSection() {
  return { id: `sec_${Math.random().toString(36).slice(2, 9)}`, title: '', body: '' };
}

export default function AIGuidelinesPage() {
  const [sections, setSections] = useState([]);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  // Test state
  const [testOpen, setTestOpen] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testSubject, setTestSubject] = useState('');
  const [testBody, setTestBody] = useState('');

  // Bulk-regenerate state
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkLimit, setBulkLimit] = useState(25);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fireToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ai/reply-guidelines`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setSections(d.sections || []);
        setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
        setDirty(false);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateSection = (idx, patch) => {
    setSections(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
    setDirty(true);
  };

  const moveSection = (idx, dir) => {
    setSections(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setDirty(true);
  };

  const removeSection = (idx) => {
    setSections(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addSection = () => {
    setSections(prev => [...prev, makeNewSection()]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/ai/reply-guidelines`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections }),
      });
      if (r.ok) {
        const d = await r.json();
        setSections(d.sections || []);
        setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
        setDirty(false);
        fireToast('success', 'Richtlijnen opgeslagen');
      } else {
        fireToast('error', 'Opslaan mislukt');
      }
    } catch {
      fireToast('error', 'Verbindingsfout');
    }
    setSaving(false);
  };

  const reset = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/ai/reply-guidelines/reset`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        const d = await r.json();
        setSections(d.sections || []);
        setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
        setDirty(false);
        fireToast('success', 'Standaardrichtlijnen hersteld');
      }
    } catch {
      fireToast('error', 'Reset mislukt');
    }
    setSaving(false);
    setConfirmReset(false);
  };

  const runTest = async () => {
    setTestRunning(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API}/api/ai/reply-guidelines/test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sample_subject: testSubject || undefined,
          sample_body: testBody || undefined,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        setTestResult(d);
      } else {
        fireToast('error', 'Test mislukt');
      }
    } catch {
      fireToast('error', 'Verbindingsfout bij test');
    }
    setTestRunning(false);
  };

  const runBulkRegenerate = async () => {
    if (!window.confirm(`Herschrijf drafts${bulkCategory ? ` in categorie "${bulkCategory}"` : ''} die wachten op review (max ${bulkLimit} totaal) met de huidige AI-richtlijnen. Doorgaan?`)) return;
    setBulkRunning(true);
    setBulkResult({ processed: 0, skipped: 0, failed: 0, total_considered: 0, remaining: '?', details: [], in_progress: true });
    const CHUNK_SIZE = 5; // max per call — onder gateway-timeout
    let totProcessed = 0, totSkipped = 0, totFailed = 0, totConsidered = 0;
    let allDetails = [];
    let runsDone = 0;
    const maxRuns = Math.ceil(bulkLimit / CHUNK_SIZE);
    try {
      while (runsDone < maxRuns) {
        const r = await fetch(`${API}/api/email/inbox/bulk-regenerate`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ category: bulkCategory || undefined, limit: CHUNK_SIZE }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          fireToast('error', `Bulk-herschrijf stopte: ${err.detail || r.statusText} (${r.status})`);
          break;
        }
        const d = await r.json();
        totProcessed += d.processed;
        totSkipped += d.skipped;
        totFailed += d.failed;
        totConsidered += d.total_considered;
        allDetails = [...allDetails, ...(d.details || [])];
        runsDone += 1;
        setBulkResult({
          processed: totProcessed, skipped: totSkipped, failed: totFailed,
          total_considered: totConsidered, remaining: d.remaining,
          details: allDetails.slice(-60), in_progress: d.total_considered > 0 && runsDone < maxRuns,
        });
        if (d.total_considered === 0) break; // Niets meer te doen
      }
      fireToast('success', `Klaar: ${totProcessed} herschreven · ${totSkipped} overgeslagen · ${totFailed} mislukt`);
      setBulkResult(b => ({ ...(b || {}), in_progress: false }));
    } catch (e) {
      fireToast('error', `Verbindingsfout: ${e.message || 'onbekend'}`);
      setBulkResult(b => ({ ...(b || {}), in_progress: false }));
    }
    setBulkRunning(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid="ai-guidelines-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2">
            <Brain size={28} weight="duotone" className="text-[#0055FF]" /> AI-richtlijnen
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Bewerk de instructies die de AI volgt bij elk concept-antwoord op inkomende e-mails
          </p>
          {meta.updated_at && (
            <p className="text-[11px] text-zinc-400 mt-2" data-testid="meta-info">
              Laatst bijgewerkt: {new Date(meta.updated_at).toLocaleString('nl-NL')}
              {meta.updated_by && <> door <strong>{meta.updated_by}</strong></>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="test-guidelines-btn"
            onClick={() => setTestOpen(v => !v)}
            className="px-4 py-2 text-xs border border-emerald-200 text-emerald-700 rounded-sm hover:bg-emerald-50 transition-colors flex items-center gap-1"
          >
            <Sparkle size={12} weight="fill" /> {testOpen ? 'Verberg test' : 'Test richtlijn'}
          </button>
          <button
            type="button"
            data-testid="reset-guidelines-btn"
            onClick={() => setConfirmReset(true)}
            disabled={saving}
            className="px-3 py-2 text-xs border border-zinc-200 text-zinc-600 rounded-sm hover:bg-zinc-50 transition-colors flex items-center gap-1"
          >
            <ArrowCounterClockwise size={12} /> Herstel standaard
          </button>
          <button
            type="button"
            data-testid="save-guidelines-btn"
            onClick={save}
            disabled={!dirty || saving}
            className="px-4 py-2 text-xs bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-40 transition-colors flex items-center gap-1"
          >
            <FloppyDisk size={12} /> {saving ? 'Bezig...' : (dirty ? 'Opslaan' : 'Opgeslagen')}
          </button>
        </div>
      </div>

      {toast && <div className="mb-4" data-testid="ai-toast"><Toast kind={toast.kind}>{toast.msg}</Toast></div>}

      {/* Test panel */}
      {testOpen && (
        <div className="mb-6 bg-emerald-50/40 border border-emerald-200 rounded-sm p-5" data-testid="test-panel">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-heading text-base font-semibold text-emerald-900 flex items-center gap-2">
                <Sparkle size={16} weight="fill" /> Test je richtlijnen op een dummy-mail
              </h3>
              <p className="text-xs text-emerald-700/80 mt-0.5">
                We sturen een voorbeeld-inkomende-mail door de AI met je huidige richtlijnen — geen klant ontvangt iets.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input
              data-testid="test-subject"
              type="text"
              value={testSubject}
              onChange={e => setTestSubject(e.target.value)}
              placeholder="Voorbeeld onderwerp (leeg = standaard)"
              className="px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-emerald-500"
            />
            <button
              type="button"
              data-testid="run-test-btn"
              onClick={runTest}
              disabled={testRunning}
              className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-1"
            >
              <Play size={14} weight="fill" /> {testRunning ? 'AI denkt na...' : 'Genereer concept'}
            </button>
          </div>
          <textarea
            data-testid="test-body"
            value={testBody}
            onChange={e => setTestBody(e.target.value)}
            rows={4}
            placeholder="Voorbeeld inkomende e-mail (leeg = renovatie-vraag uit Utrecht)"
            className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-emerald-500 resize-none"
          />

          {testResult && (
            <div className="mt-4 space-y-3" data-testid="test-result">
              <div className="bg-white border border-zinc-200 rounded-sm p-4">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Inkomende mail (sample)</div>
                <div className="text-xs font-medium text-zinc-700 mb-1">Onderwerp: {testResult.sample_inbound.subject}</div>
                <pre className="text-xs text-zinc-600 whitespace-pre-wrap font-sans">{testResult.sample_inbound.body}</pre>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
                <div className="text-[11px] uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1">
                  <Sparkle size={11} weight="fill" /> AI-gegenereerd concept-antwoord
                </div>
                {testResult.draft?.error ? (
                  <div className="text-xs text-red-700">
                    <Warning size={12} weight="fill" className="inline mr-1" />
                    {testResult.draft.error}
                  </div>
                ) : (
                  <>
                    <div className="text-xs font-medium text-emerald-900 mb-2" data-testid="draft-subject">
                      {testResult.draft?.subject || '—'}
                    </div>
                    <pre className="text-xs text-emerald-900 whitespace-pre-wrap font-sans" data-testid="draft-body">
                      {testResult.draft?.body_text || '(leeg)'}
                    </pre>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk-herschrijf panel */}
      <div className="mb-6 bg-indigo-50/40 border border-indigo-200 rounded-sm p-5" data-testid="bulk-regen-panel">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h3 className="font-heading text-base font-semibold text-indigo-900 flex items-center gap-2">
              <ArrowsClockwise size={16} weight="fill" /> Herschrijf bestaande drafts met deze richtlijnen
            </h3>
            <p className="text-xs text-indigo-700/80 mt-0.5 max-w-2xl">
              Handig nadat je richtlijnen hebt gewijzigd: laat de AI in één keer alle drafts met status
              "Wacht op review" opnieuw opstellen. Categorieën zonder draft (out-of-office, bounce, spam, afmeldingen)
              worden automatisch overgeslagen.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <select
            value={bulkCategory}
            onChange={e => setBulkCategory(e.target.value)}
            data-testid="bulk-category-select"
            disabled={bulkRunning}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-indigo-500 bg-white"
          >
            <option value="">Alle categorieën</option>
            <option value="question">Alleen vragen</option>
            <option value="quote_request">Alleen offerte-aanvragen</option>
            <option value="order">Alleen orders</option>
            <option value="interest">Alleen interesse</option>
            <option value="meeting_request">Alleen afspraakverzoeken</option>
            <option value="product_interest">Alleen productinteresse</option>
            <option value="distributor_inquiry">Alleen distributeur-vragen</option>
          </select>
          <input
            type="number"
            min={1}
            max={100}
            value={bulkLimit}
            onChange={e => setBulkLimit(Math.min(Math.max(parseInt(e.target.value) || 1, 1), 100))}
            data-testid="bulk-limit-input"
            disabled={bulkRunning}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-indigo-500"
            placeholder="Max aantal (1-100)"
          />
          <button
            type="button"
            onClick={runBulkRegenerate}
            disabled={bulkRunning}
            data-testid="bulk-regen-btn"
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {bulkRunning ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
            {bulkRunning ? 'Herschrijven...' : 'Start bulk-herschrijf'}
          </button>
        </div>
        {bulkResult && (
          <div className="bg-white border border-indigo-200 rounded-sm p-3" data-testid="bulk-regen-result">
            <div className="flex items-center gap-3 flex-wrap text-sm mb-2">
              {bulkResult.in_progress && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-indigo-100 text-indigo-800 font-semibold text-xs">
                  <CircleNotch size={11} className="animate-spin" /> Bezig...
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 font-semibold text-xs">
                <Check size={11} weight="bold" /> {bulkResult.processed} herschreven
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-zinc-100 text-zinc-600 font-semibold text-xs">
                {bulkResult.skipped} overgeslagen
              </span>
              {bulkResult.failed > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-red-100 text-red-700 font-semibold text-xs">
                  <Warning size={11} weight="fill" /> {bulkResult.failed} mislukt
                </span>
              )}
              {typeof bulkResult.remaining === 'number' && bulkResult.remaining > 0 && (
                <span className="text-xs text-indigo-700">
                  Nog {bulkResult.remaining} kandidaat{bulkResult.remaining === 1 ? '' : 'en'} over — verhoog "Max aantal" of start opnieuw
                </span>
              )}
              {typeof bulkResult.remaining === 'number' && bulkResult.remaining === 0 && !bulkResult.in_progress && (
                <span className="text-xs text-emerald-700 font-medium">Alle kandidaten verwerkt</span>
              )}
            </div>
            {(bulkResult.details || []).filter(d => d.status !== 'ok').length > 0 && (
              <details className="text-[11px] text-zinc-600">
                <summary className="cursor-pointer hover:text-indigo-700">Toon overgeslagen/mislukt details ({(bulkResult.details || []).filter(d => d.status !== 'ok').length})</summary>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1 font-data">
                  {bulkResult.details.filter(d => d.status !== 'ok').map((d, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className={`px-1 rounded-sm ${d.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-zinc-100 text-zinc-600'}`}>
                        {d.status}
                      </span>
                      <span className="text-zinc-500">{d.reply_id}</span>
                      {d.reason && <span className="text-zinc-400">— {d.reason}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Sections editor */}
      <div className="bg-white border border-zinc-200 rounded-sm" data-testid="sections-list">
        {loading ? (
          <div className="p-8 text-center text-xs text-zinc-500">Laden...</div>
        ) : sections.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">
            Nog geen secties. Klik op <strong>Sectie toevoegen</strong> om te starten.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {sections.map((s, idx) => (
              <div key={s.id} data-testid={`section-${idx}`} className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-sm bg-zinc-100 text-zinc-500 flex items-center justify-center text-[11px] font-data font-bold">
                    {idx + 1}
                  </span>
                  <input
                    data-testid={`section-title-${idx}`}
                    value={s.title}
                    onChange={e => updateSection(idx, { title: e.target.value })}
                    placeholder="Titel van deze sectie (bijv. LEVERTIJDEN)"
                    className="flex-1 px-3 py-2 text-sm font-semibold border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A] uppercase tracking-wider"
                  />
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      data-testid={`move-up-${idx}`}
                      onClick={() => moveSection(idx, -1)}
                      disabled={idx === 0}
                      title="Omhoog"
                      className="px-2 py-1.5 text-xs border border-zinc-200 text-zinc-600 rounded-sm hover:bg-zinc-50 disabled:opacity-30 transition-colors"
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      type="button"
                      data-testid={`move-down-${idx}`}
                      onClick={() => moveSection(idx, 1)}
                      disabled={idx === sections.length - 1}
                      title="Omlaag"
                      className="px-2 py-1.5 text-xs border border-zinc-200 text-zinc-600 rounded-sm hover:bg-zinc-50 disabled:opacity-30 transition-colors"
                    >
                      <ArrowDown size={12} />
                    </button>
                    <button
                      type="button"
                      data-testid={`delete-section-${idx}`}
                      onClick={() => removeSection(idx)}
                      title="Verwijder sectie"
                      className="px-2 py-1.5 text-xs border border-zinc-200 text-zinc-600 rounded-sm hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors"
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
                <textarea
                  data-testid={`section-body-${idx}`}
                  value={s.body}
                  onChange={e => updateSection(idx, { body: e.target.value })}
                  rows={Math.max(3, (s.body || '').split('\n').length + 1)}
                  placeholder="Eén regel per richtlijn — bijv: 'BEVESTIG NOOIT exacte leverdata.'"
                  className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A] font-sans"
                />
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-zinc-100 p-3 flex justify-center">
          <button
            type="button"
            data-testid="add-section-btn"
            onClick={addSection}
            className="px-4 py-2 text-xs border border-dashed border-zinc-300 text-zinc-600 rounded-sm hover:bg-zinc-50 hover:border-zinc-400 transition-colors flex items-center gap-1"
          >
            <Plus size={12} /> Sectie toevoegen
          </button>
        </div>
      </div>

      {dirty && (
        <div className="fixed bottom-4 right-4 z-40 bg-amber-50 border border-amber-200 px-4 py-2.5 text-xs text-amber-900 rounded-sm shadow-md flex items-center gap-2" data-testid="dirty-banner">
          <Warning size={14} weight="fill" />
          Niet-opgeslagen wijzigingen — vergeet niet op <strong>Opslaan</strong> te klikken
        </div>
      )}

      {/* Confirm reset */}
      {confirmReset && (
        <div
          className="fixed inset-0 bg-zinc-900/40 flex items-center justify-center p-4 z-50"
          data-testid="reset-confirm-overlay"
          onClick={() => setConfirmReset(false)}
        >
          <div className="bg-white rounded-sm border border-zinc-200 shadow-lg p-5 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h4 className="font-heading text-base font-semibold text-[#0F172A] mb-2">
              Standaard­richtlijnen herstellen?
            </h4>
            <p className="text-xs text-zinc-600 mb-5">
              Alle huidige aanpassingen worden vervangen door de originele V1.0-richtlijnen.
              Deze actie kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="reset-cancel-btn"
                onClick={() => setConfirmReset(false)}
                className="flex-1 px-3 py-2 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors"
              >
                Annuleer
              </button>
              <button
                type="button"
                data-testid="reset-confirm-btn"
                onClick={reset}
                disabled={saving}
                className="flex-1 px-3 py-2 text-xs bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-40 transition-colors flex items-center justify-center gap-1"
              >
                <Check size={12} /> Herstel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
